/**
 * useOnboardingDraft — persistenza progressiva del form di onboarding L03.
 *
 * Strategia:
 *  - Chiave isolata per token: `dietagest:onboarding:{token}`
 *  - Salvataggio debounced (500ms) ad ogni change form per non spammare disco
 *  - Recupero al mount: se presente per il token, ripristina e segnala via callback
 *  - Cleanup esplicito su submit success o errore terminale
 *  - TTL applicativo: se la draft contiene timestamp piu' vecchio di TTL_MS,
 *    viene scartata in lettura (privacy: PII non resta nel device a oltranza)
 *
 * Privacy/GDPR:
 *  - I dati salvati sono PII sensibili (clinica/identita). La chiave e' per token,
 *    quindi non condivisa tra link diversi.
 *  - Il TTL di 24h limita l'esposizione su device condivisi.
 *  - L'evento `visibilitychange` non azzera nulla (rischio perdita dati). Il
 *    cleanup avviene solo su submit o expiry.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

/** TTL massimo della draft sul device prima di essere scartata in lettura. */
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

/** Prefisso chiave localStorage (sandbox di namespace). */
const STORAGE_PREFIX = 'dietagest:onboarding:';

/** Versione schema draft (incrementa se cambia la forma payload). */
export const DRAFT_SCHEMA_VERSION = 2;

interface DraftEnvelope<T> {
  v: number;
  /** ms epoch ultima scrittura. */
  ts: number;
  data: T;
}

function storageKey(token: string): string {
  return `${STORAGE_PREFIX}${token}`;
}

/**
 * Lettura sincrona della draft (esegue una sola volta in useMemo).
 *
 * Centralizziamo qui per derivare lo stato direttamente (no setState in effect):
 *  - se manca window o token, restituiamo `null`
 *  - se schema version o TTL non validi, ripuliamo lo storage e restituiamo `null`
 *  - altrimenti restituiamo i dati draft persistiti
 */
function readDraft<T>(token: string | undefined): T | null {
  if (!token || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope<T>;
    if (parsed.v !== DRAFT_SCHEMA_VERSION) {
      window.localStorage.removeItem(storageKey(token));
      return null;
    }
    if (Date.now() - parsed.ts > TTL_MS) {
      window.localStorage.removeItem(storageKey(token));
      return null;
    }
    return parsed.data;
  } catch {
    try {
      if (token) window.localStorage.removeItem(storageKey(token));
    } catch {
      // ignore quota / disabled storage
    }
    return null;
  }
}

/**
 * Hook generico per persistere uno stato form arbitrario.
 *
 * Uso tipico:
 *   const draft = useOnboardingDraft<FormValues>(token);
 *   useEffect(() => draft.save(formState), [formState]);
 *   draft.clear() su submit.
 *
 * `initialDraft` viene letto sincronamente al primo render (via useMemo); cosi'
 * il parent puo' fare `reset(initialDraft ?? defaults)` senza setState in
 * useEffect ed evitiamo il warning React Compiler/react-hooks/set-state-in-effect.
 */
export function useOnboardingDraft<T extends object>(token: string | undefined) {
  const initialDraft = useMemo<T | null>(() => readDraft<T>(token), [token]);
  const hasDraft = initialDraft !== null;
  const saveTimerRef = useRef<number | null>(null);

  const save = useCallback(
    (data: T) => {
      if (!token || typeof window === 'undefined') return;
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        try {
          const envelope: DraftEnvelope<T> = {
            v: DRAFT_SCHEMA_VERSION,
            ts: Date.now(),
            data,
          };
          window.localStorage.setItem(
            storageKey(token),
            JSON.stringify(envelope)
          );
        } catch {
          // localStorage piena o disabilitata: degradiamo silenziosamente,
          // il form rimane funzionale, solo non c'e' persistenza.
        }
      }, 500);
    },
    [token]
  );

  const clear = useCallback(() => {
    if (!token || typeof window === 'undefined') return;
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      window.localStorage.removeItem(storageKey(token));
    } catch {
      // ignore
    }
  }, [token]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current != null) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return { initialDraft, hasDraft, save, clear };
}
